import styled from 'styled-components';
import React from 'react';
import { Affix } from 'components';
import {
  AuthContext,
  AuthContextType,
  AuthContextUser,
  LayoutContext,
  OnboardingWelcomeModalContext,
} from 'contexts';

import type { Plan } from '@bb/automaton';
import { BannerType, BANNER_HEIGHT, MOBILE_BANNER_HEIGHT } from './constants';
import AgentPlanUpgradeBanner from './AgentPlanUpgradeBanner';
import OnboardingWizardBanner from './OnboardingWizardBanner';
import PreLaunchBanner from './PreLaunchBanner';
import {
  shouldDisplayAgentPlanUpgradeBanner,
  getDisplayedPreLaunchBanner,
  shouldDisplayWizardBanner,
} from '../../onboarding/util';
import { otherElementSum } from './utils';
import { GetOnboarding } from '../../../types/getOnboardingType';
import { OrgStatus } from '../../onboarding/constants';

export const getBannerType = ({
  auth,
  orgStatus,
  onboarding,
  isDoneWelcomeFlow,
  loadingOnboarding,
  currentPlan,
  isOrgOnBrokeragePlan,
  isOrgPlanUpgraded,
}: {
  auth: AuthContextUser | undefined;
  orgStatus: OrgStatus | null;
  onboarding: GetOnboarding['onboarding'] | undefined;
  isDoneWelcomeFlow: boolean;
  loadingOnboarding: boolean;
  currentPlan: AuthContextType['orgPlan'] | null;
  isOrgOnBrokeragePlan: boolean;
  isOrgPlanUpgraded: boolean;
}): BannerType => {
  if (
    shouldDisplayAgentPlanUpgradeBanner(auth) &&
    !isOrgPlanUpgraded &&
    !isOrgOnBrokeragePlan
  ) {
    return BannerType.AgentPlanUpgrade;
  }

  if (
    !auth ||
    orgStatus === OrgStatus.ACTIVE ||
    !isDoneWelcomeFlow ||
    loadingOnboarding ||
    (!shouldDisplayAgentPlanUpgradeBanner(auth) &&
      !isOrgPlanUpgraded &&
      !isOrgOnBrokeragePlan)
  ) {
    return BannerType.None;
  }

  if (shouldDisplayWizardBanner(auth, onboarding, orgStatus))
    return BannerType.Wizard;

  if (getDisplayedPreLaunchBanner(auth, orgStatus)) return BannerType.PreLaunch;

  return BannerType.None;
};

const GlobalBanner = () => {
  const {
    auth,
    orgStatus,
    orgPlan,
    onboarding,
    loadingOnboarding,
    isOrgPlanUpgraded,
    isOrgOnBrokeragePlan,
    upgradePlanDataLoaded,
  } = React.useContext(AuthContext);

  const { topElementHeights, updateTopElementHeight, isMobileView } =
    React.useContext(LayoutContext);
  const { isDoneWelcomeFlow } = React.useContext(OnboardingWelcomeModalContext);

  const offsetTop = React.useMemo(() => otherElementSum(topElementHeights), [topElementHeights]);

  const bannerType = getBannerType({
    auth,
    orgStatus,
    currentPlan: orgPlan,
    onboarding,
    isDoneWelcomeFlow,
    loadingOnboarding,
    isOrgOnBrokeragePlan,
    isOrgPlanUpgraded,
  });

  const bannerHeight = isMobileView ? MOBILE_BANNER_HEIGHT : BANNER_HEIGHT;

  React.useEffect(() => {
    if (!upgradePlanDataLoaded) return;
    switch (bannerType) {
      case BannerType.PreLaunch:
      case BannerType.AgentPlanUpgrade:
      case BannerType.Wizard:
        updateTopElementHeight('globalBanner', bannerHeight);
        break;
      default:
        updateTopElementHeight('globalBanner', 0);
    }
    return () => {
      updateTopElementHeight('globalBanner', 0);
    };
  }, [bannerType, updateTopElementHeight, bannerHeight, upgradePlanDataLoaded]);

  if (!upgradePlanDataLoaded || bannerType === BannerType.None) return null;

  const renderBanner = () => {
    switch (bannerType) {
      case BannerType.AgentPlanUpgrade:
        return <AgentPlanUpgradeBanner />;
      case BannerType.Wizard:
        return <OnboardingWizardBanner />;
      case BannerType.PreLaunch:
        return <PreLaunchBanner />;
      default:
        return null;
    }
  };

  return (
    <>
      <StyledAffix offsetTop={offsetTop} $height={bannerHeight}>
        <div>{renderBanner()}</div>
      </StyledAffix>
    </>
  );
};

export default GlobalBanner;

const StyledAffix = styled(Affix)<{ $height: number }>`
  ${({ $height }) => `height: ${$height}px`}
  overflow: hidden;
  position: fixed;
  width: 100%;
  z-index: 1000; // same as the header
`;
